import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {BOOKING_CLASS_TYPES} from "@/config/constants";
import type {BookingHistoryItem} from "@/services/livingIn/booking/bookingApiService";

interface ClassTypeOption {
  label: string;
  value: string;
}

interface BookingResponse {
  createdDate: string;
  appointmentId: string;
  status: string;
  referenceNumber: string;
  transactionId?: string;
}

interface BookingState {
  currentStep: number;
  classDetails: Record<string, any> | null;
  bookingData: {
    classId: string;
    className: string;
    location: string;
    participantSchedules: Record<string, any>;
    classType: ClassTypeOption | null;
    selectedPackage: any;
    participantCount: number;
    adultsCount: number;
    childrenCount: number;
  };
  bookingResponse: BookingResponse | null;
  selectedBooking: BookingHistoryItem | null;
}

const initialState: BookingState = {
  currentStep: 0,
  classDetails: null,
  bookingData: {
    classId: "",
    className: "",
    location: "",
    participantSchedules: {},
    classType: null,
    selectedPackage: "",
    participantCount: 0,
    adultsCount: 0,
    childrenCount: 0,
  },
  bookingResponse: null,
  selectedBooking: null,
};

const bookingJourneySlice = createSlice({
  name: "bookingJourneySlice",
  initialState: initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      const maxSteps =
        state.bookingData.classType?.value === BOOKING_CLASS_TYPES.FREE_TRIAL ? 4 : 8;
      const newStep = Math.max(0, Math.min(action.payload, maxSteps));
      state.currentStep = newStep;
    },
    nextStep: (state) => {
      // Allow up to 3 steps for trial classes, up to 7 steps for private/group classes
      const maxSteps =
        state.bookingData.classType?.value === BOOKING_CLASS_TYPES.FREE_TRIAL ? 4 : 8;
      state.currentStep = Math.min(state.currentStep + 1, maxSteps);
    },
    previousStep: (state) => {
      state.currentStep = Math.max(state.currentStep - 1, 0);
    },
    setBookingData: (state, action) => {
      state.bookingData = {
        ...state.bookingData,
        ...action.payload,
        participantSchedules: {
          ...state.bookingData.participantSchedules,
          ...(action.payload.participantSchedules || {}),
        },
      };
    },
    setClassType: (state, action: PayloadAction<ClassTypeOption | null>) => {
      state.bookingData.classType = action.payload;
    },
    setAdultsCount: (state, action: PayloadAction<number>) => {
      state.bookingData.adultsCount = Math.max(0, action.payload);
      state.bookingData.participantCount = Math.max(0, action.payload) + state.bookingData.childrenCount;
    },
    setChildrenCount: (state, action: PayloadAction<number>) => {
      state.bookingData.childrenCount = Math.max(0, action.payload);
      state.bookingData.participantCount = state.bookingData.adultsCount + Math.max(0, action.payload);
    },
    setParticipantSchedule: (
      state,
      action: PayloadAction<{participantId: string; schedule: any}>,
    ) => {
      if (!state.bookingData.participantSchedules) {
        state.bookingData.participantSchedules = {};
      }
      state.bookingData.participantSchedules[action.payload.participantId] =
        action.payload.schedule;
    },
    setSelectedPackage: (state, action) => {
      state.bookingData.selectedPackage = action.payload;
    },
    clearParticipantSchedules: (state) => {
      state.bookingData.participantSchedules = {};
    },
    setClassDetails: (state, action: PayloadAction<Record<string, any> | null>) => {
      state.classDetails = action.payload;
    },
    setBookingResponse: (state, action: PayloadAction<BookingResponse | null>) => {
      state.bookingResponse = action.payload;
    },
    setBookingTransactionId: (state, action: PayloadAction<string>) => {
      if (state.bookingResponse) {
        state.bookingResponse.transactionId = action.payload;
      }
    },
    goToPaymentStep: (state) => {
      state.currentStep = 6;
    },
    setSelectedBooking: (
      state,
      action: PayloadAction<BookingHistoryItem | null>,
    ) => {
      state.selectedBooking = action.payload;
    },
    resetBooking: () => {
      return {...initialState};
    },
  },
});

export const {
  setCurrentStep,
  nextStep,
  previousStep,
  setBookingData,
  setClassType,
  setAdultsCount,
  setChildrenCount,
  setParticipantSchedule,
  setSelectedPackage,
  clearParticipantSchedules,
  setClassDetails,
  setBookingResponse,
  setBookingTransactionId,
  goToPaymentStep,
  setSelectedBooking,
  resetBooking,
} = bookingJourneySlice.actions;

export default bookingJourneySlice.reducer;
